import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';

const DISMISS_KEY = 'pwa_install_banner_dismissed_at';
const DISMISS_COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

export default function InstallPWABanner() {
  const { siteName } = useSiteSettings();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosInstructions, setIosInstructions] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    if (isStandalone) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    // Safari on iOS/iPadOS never fires beforeinstallprompt - there's no
    // programmatic install API there at all, so show manual instructions
    // instead of silently never prompting these users.
    const isIos = /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase()) && !window.MSStream;
    if (isIos) {
      setIosInstructions(true);
      setVisible(true);
      return;
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    const handleAppInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-bots-surface border-t border-bots-accent text-bots-text px-4 py-3 shadow-tech"
      data-testid="pwa-install-banner"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Download className="w-5 h-5 flex-shrink-0 text-bots-highlight" />
          <p className="text-sm truncate">
            {iosInstructions
              ? `Install ${siteName || 'this app'}: tap the Share icon, then "Add to Home Screen".`
              : `Install ${siteName || 'this app'} on your device for quick access.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!iosInstructions && (
            <button
              onClick={handleInstall}
              className="px-4 py-1.5 bg-blue-gradient text-white text-sm font-semibold rounded-md hover:opacity-90 transition-opacity"
              data-testid="pwa-install-button"
            >
              Install
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/10 rounded-md transition-colors"
            aria-label="Dismiss"
            data-testid="pwa-install-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
